import TitleBar from '../../../../components/TitleBar';
import { ColumnsType } from 'antd/es/table';
import { Table, message } from 'antd';
import { Footer } from '../../../../components/common/Footer';
import { Button } from '../../../../components';
import { useMutation, useQuery } from '@apollo/client';
import { useNavigate, useParams } from 'react-router-dom';
import {
  CREATE_DIVISION,
  DELETE_DIVISION,
  EDIT_DIVISION,
  FETCH_DIVISIONS,
} from '../../queries';
import { SPECIAL_CHAR_REGEX } from '../../../../utils/regex';
import { tableSortingMethods } from '../../../../utils';
import TableV3, {
  ModifyVariablesArgs,
} from '../../../../components/generic/Table/TableV3';
import FinancialData from '../../../../components/common/FinancialData';
import { formFields } from './formFields';
import { baseColumns } from './exportColumns';
import { useUserContext } from '../../../../context/PermissionsContext';
import QueryErrorBoundary from '../../../../components/error-handling';
import { getFilteredTags } from '../../../../utils/getFilteredTags';
import TitleUpdater from '../../../../components/common/TitleUpdater';
import { ErrorMessages } from '../../../../constants';

type FindAllDivisions = {
  name: string;
  revenue: number;
  costOfSales: number;
  grossMargin: number;
  uuid: string;
  isDefault: boolean;
};

interface DataReturned {
  findAllDivisions: FindAllDivisions[];
}

function DivisionFinancials() {
  const params = useParams();
  const corporateId = params.corporateId!;
  const corporateName = params.corporateName!;
  const navigate = useNavigate();

  const { canViewFinancials, selectedTags, isDivision } = useUserContext();

  const { data, loading, error } = useQuery(FETCH_DIVISIONS);

  const [createDivision] = useMutation(CREATE_DIVISION, {
    refetchQueries: ['GetDivisions', 'DivisionSelectionDashboard'],
    onError: (error) =>
      message.error(
        error instanceof Error && error.message
          ? error.message
          : ErrorMessages.OPERATION_FAILED,
      ),
  });

  const [updateDivision] = useMutation(EDIT_DIVISION, {
    refetchQueries: ['GetDivisions', 'DivisionSelectionDashboard'],
    onError: (error) => {
      message.error(
        error instanceof Error && error.message
          ? error.message
          : ErrorMessages.OPERATION_FAILED,
      );
    },
  });

  const [deleteDivision] = useMutation(DELETE_DIVISION, {
    refetchQueries: ['GetDivisions', 'DivisionSelectionDashboard'],
  });

  const transformedData = data?.findAllDivisions.filter((item: any) => {
    return item.isDefault === false;
  });

  const filteredDivisions = getFilteredTags({
    data: transformedData,
    selectedTags,
    condition: isDivision,
    lookUpKey: 'uuid',
  });

  const columns: ColumnsType<any> = [
    {
      title: 'Division',
      dataIndex: 'name',
      key: 'name',
      align: 'left',
      width: 200,
      sorter: (a, b) => tableSortingMethods.string(a.name, b.name),
      render: (text: string) => (
        <span className="custom-table--column">{text}</span>
      ),
    },
    {
      title: 'Revenue',
      dataIndex: 'revenue',
      key: 'revenue',
      align: 'right',
      width: 100,
      sorter: canViewFinancials
        ? (a, b) => tableSortingMethods.number(a.revenue, b.revenue)
        : false,
      render: (text: number) => (
        <span className="custom-table--column">
          <FinancialData text={text} />
        </span>
      ),
    },
    {
      title: 'Cost of Sales',
      dataIndex: 'costOfSales',
      key: 'costOfSales',
      align: 'right',
      width: 100,
      sorter: canViewFinancials
        ? (a, b) => tableSortingMethods.number(a.costOfSales, b.costOfSales)
        : false,
      render: (text: number) => (
        <span className="custom-table--column">
          <FinancialData text={text} />
        </span>
      ),
    },
    {
      title: 'Gross Margin',
      key: 'grossMargin',
      dataIndex: 'grossMargin',
      align: 'right',
      width: 100,
      sorter: canViewFinancials
        ? (a, b) => tableSortingMethods.number(a.grossMargin, b.grossMargin)
        : false,
      render: (text: number) => (
        <span className="custom-table--column">
          <FinancialData text={text} />
        </span>
      ),
    },
  ];

  const extendedActions = (record: any) => {
    const nameParts = record?.name?.split(SPECIAL_CHAR_REGEX);
    const formattedName = nameParts.join('-').toLowerCase();

    return (
      <Button
        type="primary-rounded"
        className="text-xsm"
        onClick={() =>
          navigate(
            `/${corporateName}/${corporateId}/${formattedName}/${record.uuid}`,
          )
        }
      >
        Product Groups
      </Button>
    );
  };

  const tableSummary = () => {
    const defaultRow = data?.findAllDivisions?.filter(
      (item: any) => item.isDefault,
    )!;

    if (!defaultRow) return;

    return (
      <Table.Summary.Row>
        <Table.Summary.Cell index={0} className="custom-table--column text-sm">
          {defaultRow[0].name}
        </Table.Summary.Cell>
        <Table.Summary.Cell
          index={1}
          align={'right'}
          className="custom-table--column text-sm"
        >
          <FinancialData text={defaultRow[0]?.revenue} />
        </Table.Summary.Cell>
        <Table.Summary.Cell
          index={2}
          align={'right'}
          className="custom-table--column text-sm"
        >
          <FinancialData text={defaultRow[0]?.costOfSales} />
        </Table.Summary.Cell>
        <Table.Summary.Cell
          index={3}
          align={'right'}
          className="custom-table--column text-sm"
        >
          <FinancialData text={defaultRow[0]?.grossMargin} />
        </Table.Summary.Cell>
      </Table.Summary.Row>
    );
  };

  const modifyVariables = ({
    values,
    type,
    currentRecord,
  }: ModifyVariablesArgs) => {
    const revenue = parseFloat(String(values.revenue).replace(/,/g, ''));
    const costOfSales = parseFloat(
      String(values.costOfSales).replace(/,/g, ''),
    );

    if (type === 'create') {
      return {
        ...values,
        corporateFinancial: { uuid: params.corporateId },
        revenue: revenue,
        costOfSales: costOfSales,
        grossMargin: 0,
        name: values.name,
      };
    }

    return {
      ...values,
      revenue: revenue,
      costOfSales: costOfSales,
      grossMargin: currentRecord.grossMargin,
      name: values.name,
    };
  };

  return (
    <>
      <TitleUpdater title="Division Financials" />
      <TitleBar
        title="Data Inputs"
        subtitle={`Financials // ${corporateName}`}
        brief="Divisions can vary dramatically in terms of size, margins, and the products they make.
                      Providing more granular financials allows for more accurate quantification of risk and supports
                      effective risk management."
      />
      <QueryErrorBoundary error={error}>
        <TableV3
          title="Division Financials"
          columns={columns}
          data={filteredDivisions}
          loading={loading}
          editable
          summary={tableSummary}
          formFields={formFields}
          createGQL={createDivision}
          updateGQL={updateDivision}
          deleteGQL={deleteDivision}
          modifyVariables={modifyVariables}
          editableOptions={{
            create: canViewFinancials,
            update: canViewFinancials,
            delete: canViewFinancials,
          }}
          extendActions={extendedActions}
          inlineEditing
          exportExcelColumns={baseColumns}
        />
      </QueryErrorBoundary>
      <Footer backPath={`/financials-corporate`} />
    </>
  );
}

export default DivisionFinancials;
